/* =====================================================================
 * cust-v6-sticky-buy.js — wires the mobile sticky bar to the real ATC
 *
 * The bar lives outside the product form. This script:
 *   1. Finds the real [data-add-to-cart] button rendered by Stiletto's
 *      product-form snippet.
 *   2. On sticky-button click, proxies the click to the real button so
 *      Shopify's existing add-to-cart pipeline runs unchanged (variant
 *      validation, AJAX submit, cart drawer, Recharge selling-plan
 *      attachment, _persona_id cart attribute writer, all of it).
 *   3. Mirrors the real button's disabled state + visible label into
 *      the sticky button via a MutationObserver.
 *   4. Re-runs on shopify:section:load so theme-editor section reloads
 *      don't leave a dead bar behind. (feedback_section_reload_safety.md)
 * ===================================================================== */
(function () {
  'use strict';

  var BOUND_FLAG = '__v6StickyBuyBound';

  function init(scope) {
    var root = scope || document;
    var bars = root.querySelectorAll('.v6-sticky-buy');
    if (!bars.length) return;

    bars.forEach(function (bar) {
      if (bar[BOUND_FLAG]) return;
      bar[BOUND_FLAG] = true;

      var ctaSticky = bar.querySelector('[data-v6-sticky-cta]');
      if (!ctaSticky) return;

      // Find the page's primary ATC. Default PDP uses [data-add-to-cart]
      // (Stiletto product-form). Shampoo/Conditioner PDP uses
      // [data-atc-button] (custom Single/Duo/Trio controller). Prefer the
      // custom one when present so the sticky bar follows the live total.
      function findRealCta() {
        return (
          document.querySelector('[data-atc-button]') ||
          document.querySelector('[data-product-form] [data-add-to-cart]') ||
          document.querySelector('[data-add-to-cart]')
        );
      }

      // On Shampoo/Conditioner PDPs the live total lives in [data-total]
      // and Stiletto's [data-price] updater is not wired. We mirror that
      // element's text into the sticky bar's [data-price] span.
      //
      // On the default PDP we do NOTHING — Stiletto's variant-change
      // handler already updates every [data-price] node on the page,
      // and our sticky bar's span has data-price too, so it follows
      // along for free. Mirroring [data-product-form] [data-price]
      // here was overwriting the sticky price with the wrong text.
      function findPriceSource() {
        return document.querySelector('[data-total]');
      }

      function syncPrice() {
        var priceNode = bar.querySelector('[data-price]');
        var src = findPriceSource();
        if (!priceNode || !src || src === priceNode) return;

        // [data-total] on shampoo-conditioner PDPs renders as
        //   <span class="t-strike">$11.75</span>$9.40
        // when a sale is active. textContent would concatenate both
        // into "$11.75$9.40". Strip the strike span first so the
        // sticky bar shows only the current price.
        var clone = src.cloneNode(true);
        var strikes = clone.querySelectorAll('.t-strike, s, del, [data-compare-price]');
        for (var i = 0; i < strikes.length; i++) {
          strikes[i].parentNode.removeChild(strikes[i]);
        }
        var t = (clone.textContent || '').trim();
        if (t) priceNode.textContent = t;
      }

      function syncState() {
        var real = findRealCta();
        if (!real) {
          ctaSticky.setAttribute('disabled', '');
          ctaSticky.setAttribute('aria-disabled', 'true');
          return;
        }
        var disabled =
          real.hasAttribute('disabled') ||
          real.getAttribute('aria-disabled') === 'true';
        if (disabled) {
          ctaSticky.setAttribute('disabled', '');
          ctaSticky.setAttribute('aria-disabled', 'true');
        } else {
          ctaSticky.removeAttribute('disabled');
          ctaSticky.removeAttribute('aria-disabled');
        }

        // Mirror the real button's label into our [data-v6-sticky-label]
        // span ONLY. The sticky button now contains label + separator +
        // price spans, so a blanket textContent write would wipe them.
        var labelNode = real.querySelector('[data-add-to-cart-text]') || real;
        var labelText = (labelNode.textContent || '').trim();
        var stickyLabel = ctaSticky.querySelector('[data-v6-sticky-label]');
        if (labelText && stickyLabel) {
          stickyLabel.textContent = labelText;
        }
      }

      ctaSticky.addEventListener('click', function (e) {
        e.preventDefault();
        var real = findRealCta();
        if (!real || real.hasAttribute('disabled')) return;
        real.click();
      });

      syncState();
      syncPrice();

      // Watch the real button for attribute + text changes.
      var real = findRealCta();
      if (real) {
        var btnObs = new MutationObserver(syncState);
        btnObs.observe(real, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
          childList: true,
          characterData: true,
          subtree: true
        });
      }

      // Watch the live price source for text changes (variant /
      // Single-Duo-Trio / Subscribe-OneTime updates).
      var priceSrc = findPriceSource();
      if (priceSrc) {
        var priceObs = new MutationObserver(syncPrice);
        priceObs.observe(priceSrc, {
          childList: true,
          characterData: true,
          subtree: true
        });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (evt) {
    init(evt.target);
  });
})();
